/**
 * Cross-site sign-out helpers.
 *
 * Why this exists:
 *   Our SDK's logout() does Cognito /logout for the current SPA's client_id,
 *   then redirects back to the app origin. On reload, useSsoCheck immediately
 *   runs a silent SSO check (prompt=none) against the central Cognito session.
 *   If the central session cookie wasn't fully cleared (or another client's
 *   session is still alive), the user gets silently re-authenticated — which
 *   feels like "logout did nothing".
 *
 *   We solve this with a short-lived localStorage flag set BEFORE the SDK
 *   logout fires. After the post-logout redirect, installSignedOutGuard()
 *   (called early in main.tsx) sees the flag and pre-sets the SSO-checked
 *   sessionStorage marker so useSsoCheck skips its silent check exactly once.
 */

import { useCallback, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@codespeak11/bdance-theme';
import { useAuth } from '@codespeak11/cognito-passwordless';

const SIGNED_OUT_AT_KEY = 'bdance_signed_out_at';
const SSO_CHECKED_KEY = 'bdance_sso_checked';
const GUARD_WINDOW_MS = 30_000;

/**
 * Call once from main.tsx BEFORE configureCognito() / AuthProvider.
 * Suppresses the next silent SSO check if the user just explicitly signed out.
 */
export function installSignedOutGuard(): void {
  if (typeof window === 'undefined') return;
  const raw = localStorage.getItem(SIGNED_OUT_AT_KEY);
  if (!raw) return;
  localStorage.removeItem(SIGNED_OUT_AT_KEY);
  const ts = Number.parseInt(raw, 10);
  if (Number.isFinite(ts) && Date.now() - ts < GUARD_WINDOW_MS) {
    sessionStorage.setItem(SSO_CHECKED_KEY, 'true');
  }
}

interface UseSignOutConfirmReturn {
  /** Open the confirmation dialog. Pass to onSignOut on UserDropdownMenu. */
  requestSignOut: () => void;
  /** Render this somewhere in your tree. */
  dialog: ReactElement;
}

/**
 * Hook that returns a sign-out request handler and the ConfirmDialog to render.
 * On confirm, sets the cross-site signed-out marker and calls SDK logout().
 */
export function useSignOutConfirm(): UseSignOutConfirmReturn {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestSignOut = useCallback(() => setOpen(true), []);
  const cancel = useCallback(() => {
    if (!loading) setOpen(false);
  }, [loading]);
  const confirm = useCallback(async () => {
    setLoading(true);
    try {
      localStorage.setItem(SIGNED_OUT_AT_KEY, String(Date.now()));
      await logout();
    } finally {
      // SDK logout does a full-page redirect; this only runs if it failed.
      setLoading(false);
      setOpen(false);
    }
  }, [logout]);

  const dialog = (
    <ConfirmDialog
      open={open}
      title="Sign out of all BrainDance sites?"
      message="Signing out will end your session across all BrainDance websites (BrainDance, PromptFab, PuzzLearn, DynamicBooks, and others). You'll need to sign in again on each site to continue."
      confirmLabel="Sign out"
      cancelLabel="Cancel"
      variant="warning"
      loading={loading}
      onConfirm={confirm}
      onCancel={cancel}
    />
  );

  return { requestSignOut, dialog };
}
